import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, Autocomplete, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  HourglassBottom as HourglassIcon,
  Groups as GroupsIcon,
  MeetingRoom as MeetingRoomIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';

// ─── Tipos ───

interface Totais {
  total_pacientes: number;
  espera_media_seg: number;
  espera_max_seg: number;
  atendimento_medio_seg: number;
  salas_ativas: number;
}

interface PorSala {
  sala: string;
  pacientes: number;
  espera_media_seg: number;
}

interface ListaItem {
  id_recepcao: number;
  nompaciente: string;
  idade: number | null;
  sala: string;
  dh_recepcao_fmt: string;
  dh_atendimento_fmt: string;
  espera_seg: number;
  atendimento_seg: number;
  nomespecialidade: string;
  nomprofissional: string;
}

interface FilasEspera {
  totais: Totais;
  por_sala: PorSala[];
  lista: ListaItem[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

interface Sala {
  id_sala: number;
  nomsala: string;
}

const REFRESH_MS = 60_000;
const BAR_COLORS = ['#1976d2', '#43a047', '#ff9800', '#7b1fa2', '#00838f', '#e53935', '#6d4c41', '#546e7a'];

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/** Formata segundos em texto amigavel: "2h 35min", "12 min", "<1 min". */
const fmtDuracao = (seg?: number | null): string => {
  if (seg == null || Number.isNaN(seg) || seg <= 0) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
  if (m > 0) return `${m} min`;
  return '<1 min';
};

// ─── KPI Card (valor textual ja formatado) ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeFilasEspera: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<FilasEspera | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtros
  const [dataInicio, setDataInicio] = useState(toISO(new Date()));
  const [dataFim, setDataFim] = useState(toISO(new Date()));
  const [salas, setSalas] = useState<Sala[]>([]);
  const [salasLoading, setSalasLoading] = useState(false);
  const [salaSelecionada, setSalaSelecionada] = useState<Sala | null>(null);

  const fetchRef = useRef<() => void>(() => {});

  // Lookup de salas: so e preenchido quando ha uma unidade selecionada
  useEffect(() => {
    setSalaSelecionada(null);
    if (!municipioAtivo?.id || !unidadeSelecionada?.codunidade) {
      setSalas([]);
      return;
    }
    let cancelled = false;
    setSalasLoading(true);
    saudeService
      .getSalas(municipioAtivo.id, unidadeSelecionada.codunidade)
      .then((d: Sala[]) => { if (!cancelled) setSalas(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setSalas([]); })
      .finally(() => { if (!cancelled) setSalasLoading(false); });
    return () => { cancelled = true; };
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getFilasEspera(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        sala: salaSelecionada ? String(salaSelecionada.id_sala) : undefined,
        inicio: dataInicio,
        fim: dataFim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar filas e tempo de espera.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, salaSelecionada, dataInicio, dataFim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar qualquer filtro
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  const totais = data?.totais;
  const lista = data?.lista || [];

  const exportCsv = useCallback(() => {
    if (!lista.length) return;
    const header = ['Paciente', 'Idade', 'Sala', 'Recepcao', 'Atendimento', 'Espera', 'Duracao atend.', 'Especialidade', 'Profissional'];
    const linhas = lista.map((r) => [
      r.nompaciente, r.idade ?? '', r.sala, r.dh_recepcao_fmt, r.dh_atendimento_fmt,
      fmtDuracao(r.espera_seg), fmtDuracao(r.atendimento_seg), r.nomespecialidade, r.nomprofissional,
    ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'));
    const csv = [header.join(';'), ...linhas].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `filas-espera-${dataInicio}_${dataFim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [lista, dataInicio, dataFim]);

  const SalaTooltip = useMemo(() => ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as PorSala;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
        <Typography variant="caption" fontWeight={600} display="block">{d.sala}</Typography>
        <Typography variant="caption" display="block">Pacientes: {d.pacientes}</Typography>
        <Typography variant="caption" display="block">Espera media: {fmtDuracao(d.espera_media_seg)}</Typography>
      </Box>
    );
  }, [theme.palette.divider]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar as filas e tempo de espera.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HourglassIcon color="primary" /> Filas e Espera
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong>{salaSelecionada ? <> • Sala: <strong>{salaSelecionada.nomsala}</strong></> : ''}</>
              : 'Todas as unidades do municipio'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtros: periodo + sala */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={handleDateChange} />
          <Autocomplete
            size="small"
            options={salas}
            getOptionLabel={(o) => o.nomsala}
            value={salaSelecionada}
            onChange={(_, v) => setSalaSelecionada(v)}
            disabled={!unidadeSelecionada}
            loading={salasLoading}
            isOptionEqualToValue={(o, v) => o.id_sala === v.id_sala}
            sx={{ width: 240 }}
            renderInput={(params) => (
              <TextField {...params} label="Sala"
                placeholder={unidadeSelecionada ? 'Todas as salas' : 'Selecione a unidade'} />
            )}
          />
          {!unidadeSelecionada && (
            <Typography variant="caption" color="text.secondary">
              Selecione uma Unidade de Saude (acima) para filtrar por sala.
            </Typography>
          )}
        </CardContent>
      </Card>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}><KpiCard label="Pacientes" value={(totais?.total_pacientes ?? 0).toLocaleString('pt-BR')} icon={<GroupsIcon />} color="#1976d2" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Tempo medio de espera" value={fmtDuracao(totais?.espera_media_seg)} icon={<TimerIcon />} color="#ff9800" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Maior espera" value={fmtDuracao(totais?.espera_max_seg)} icon={<TrendingUpIcon />} color="#e53935" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Tempo medio de atendimento" value={fmtDuracao(totais?.atendimento_medio_seg)} icon={<HourglassIcon />} color="#43a047" loading={loading} /></Grid>
        <Grid item xs={6} md={2.4}><KpiCard label="Salas ativas" value={(totais?.salas_ativas ?? 0).toLocaleString('pt-BR')} icon={<MeetingRoomIcon />} color="#00838f" loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Por sala */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Pacientes por sala</Typography>
              {loading ? <Skeleton variant="rectangular" height={320} /> : (data?.por_sala.length ? (
                <ResponsiveContainer width="100%" height={Math.max(320, data.por_sala.length * 40)}>
                  <BarChart data={data.por_sala} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="sala" width={150} tick={{ fontSize: 11 }} interval={0} />
                    <RechartsTooltip content={<SalaTooltip />} />
                    <Bar dataKey="pacientes" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }}>
                      {data.por_sala.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem pacientes no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Lista individual */}
        <Grid item xs={12} lg={7}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Tempo de espera individual {lista.length >= 500 ? '(500 mais recentes)' : ''}
                </Typography>
                <Tooltip title="Exportar CSV">
                  <span>
                    <IconButton size="small" onClick={exportCsv} disabled={!lista.length}><FileDownloadIcon fontSize="small" /></IconButton>
                  </span>
                </Tooltip>
              </Box>
              {loading ? <Skeleton variant="rectangular" height={360} /> : (lista.length ? (
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Paciente</TableCell>
                        <TableCell>Sala</TableCell>
                        <TableCell>Recepcao</TableCell>
                        <TableCell>Atendimento</TableCell>
                        <TableCell align="right">Espera</TableCell>
                        <TableCell>Profissional</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lista.map((r) => (
                        <TableRow key={r.id_recepcao} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {r.nompaciente}{r.idade != null ? <Typography component="span" variant="caption" color="text.secondary"> ({r.idade}a)</Typography> : ''}
                          </TableCell>
                          <TableCell>{r.sala}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.dh_recepcao_fmt}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.dh_atendimento_fmt}</TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={fmtDuracao(r.espera_seg)}
                              color={r.espera_seg >= 3600 ? 'error' : r.espera_seg >= 1800 ? 'warning' : 'success'}
                              variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.nomprofissional}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem pacientes no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Periodo: ${data?.periodo?.inicio ?? dataInicio} a ${data?.periodo?.fim ?? dataFim} • Espera = recepcao ate o atendimento`} />
    </Box>
  );
};

export default SaudeFilasEspera;
